import {
	Box,
	Button,
	Checkbox,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import "./modalSelectNurseBody.scss";
import { PatientsListContext } from "../../../components/PatientsList/PatientsList";
import { useContext, useEffect, useState } from "react";
import { getHeaders, URI_API } from "../../..//utils/config";
import Shift from "../../../utils/shiftFunctions";
import { NursePriority } from "../../../utils/nursePriority";
import { ArrowRight, Search, Square, SquareCheck, User } from "lucide-react";
import variables from "../../../styles/variables.module.scss";
import { ReactComponent as EmtyInfoIcon } from "../../../assets/icons/patients/noun-nurse.svg";
import CustomSnackbar, { SnackbarModes } from "../../../widgets/Snackbars/Snackbar/Snackbar";
import { useDispatch } from "react-redux";
import { setPageState } from "../../../store/reducer";
import { PageState } from "../../../views/Home/HomeUtils";
import { red } from "@mui/material/colors";

interface ModalSelectNurseBodyProps {
	handleTab: () => void;
}

export default function ModalSelectNurseBody({ handleTab }: ModalSelectNurseBodyProps) {
	const {
		selectedPatient,
		setSelectedPatient,
		openNurseCategoryAlert,
		setOpenNurseCategoryAlert,
		setOpenAssignNurseModal,
		setAvailableNursesContext,
	} = useContext(PatientsListContext);

	const [nurses, setNurses] = useState<any>([]);
	const [filteredNurses, setFilteredNurses] = useState<any>([]);
	const [selectednurse_id, setSelectednurse_id] = useState<string>(
		selectedPatient.nurse_id ?? "",
	);
	const [selectedNurseName, setSelectedNurseName] = useState<string>("");
	const [searchInput, setSearchInput] = useState<string>("");
	const [isNursesEmpty, setIsNursesEmpty] = useState<boolean>(false);
	const [isDisabled, setIsDisabled] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(
		null,
	);

	useEffect(() => {
		setIsDisabled(selectednurse_id === "");
	}, [selectednurse_id]);

	const fetchSchedule = async (date: any) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0"); // Los meses empiezan desde 0
		const day = String(date.getDate()).padStart(2, "0");

		const dateToSearch = `${year}-${month}-${day}`;
		return await (
			await fetch(`${URI_API}/work_schedule_name_by_day/${dateToSearch}`, getHeaders())
		).json();
	};
	const getNurseInformation = async () => {
		const date = new Date();
		const pad = (num: any) => num.toString().padStart(2, "0");
		const year = date.getFullYear();
		const month = pad(date.getMonth() + 1);
		const day = pad(date.getDate());

		const midnight = new Date(`${year}-${month}-${day}T00:00:00.000-06:00`);
		const default_departure_time = new Date(`${year}-${month}-${day}T07:30:00.000-06:00`);
		let currentShift, schedulesRes;
		// Night shift flow
		if (date > midnight && date < default_departure_time) {
			const yesterday = new Date(date.getTime() - 86400000);
			schedulesRes = await fetchSchedule(yesterday);
			if (schedulesRes[0].schedule_name.includes("nocturno")) {
				currentShift = Shift.AsOption(Shift.Current(schedulesRes));
			} else {
				schedulesRes = await fetchSchedule(date);
				currentShift = schedulesRes[0].schedule_name.includes("nocturno")
					? null
					: Shift.AsOption(Shift.Current(schedulesRes));
			}
			// Non-night shift flow
		} else {
			schedulesRes = await fetchSchedule(date);
			currentShift = Shift.AsOption(Shift.Current(schedulesRes));
		}

		// Get Classification Matrix
		let matrix_res = await (
			await fetch(`${URI_API}/class_category_matrix`, getHeaders())
		).json();

		// const today_departure_time = new Date(date.getFullYear() + '-' + ((date.getMonth()) + 1) + '-' + date.getDate() + 'T' + currentShift?.departure_time + ':00.000-06:00');
		const today_departure_time = new Date(
			`${year}-${month}-${day}T${currentShift?.departure_time}:00.000-06:00`,
		);
		let date_str;
		if (
			date > midnight &&
			date < today_departure_time &&
			currentShift?.key.toLowerCase().includes("nocturno")
		) {
			const date_str_raw = new Date(date.getTime() - 86400000);
			const year_raw = date_str_raw.getFullYear();
			const month_raw = String(date_str_raw.getMonth() + 1).padStart(2, "0"); // Los meses empiezan desde 0
			const day_raw = String(date_str_raw.getDate()).padStart(2, "0");

			date_str = `${year_raw}-${month_raw}-${day_raw}`;
		} else {
			const year_raw = date.getFullYear();
			const month_raw = String(date.getMonth() + 1).padStart(2, "0"); // Los meses empiezan desde 0
			const day_raw = String(date.getDate()).padStart(2, "0");
			date_str = `${year_raw}-${month_raw}-${day_raw}`;
		}

		let nurses = await (
			await fetch(
				`${URI_API}/nursing_calendar_sch/${date_str}/${currentShift?.key}/${selectedPatient.area_id}`,
				getHeaders(),
			)
		).json();

		let availableNurses = NursePriority(
			schedulesRes.length,
			selectedPatient,
			nurses,
			matrix_res,
		);
		setAvailableNursesContext(availableNurses);
		let availableSelectedNurses: any[] = [];
		availableNurses.forEach((nurse: any) => {
			if (
				nurse.classx == "COORD" ||
				nurse.classx == "ENF A" ||
				nurse.classx == "ENF B" ||
				nurse.classx == "ENF C" ||
				nurse.classx == "ENF ESP" ||
				nurse.classx == "ENF SUB" ||
				nurse.classx == "ENF D"
			)
				availableSelectedNurses.push(nurse);
		});

		setNurses(availableSelectedNurses);
		setFilteredNurses(availableSelectedNurses);
		setIsNursesEmpty(availableSelectedNurses.length === 0);
		setIsLoading(false);
	};

	useEffect(() => {
		if (nurses.length === 0) getNurseInformation();
	}, []);

	useEffect(() => {
		if (searchInput === "") setFilteredNurses(nurses);
		else {
			setFilteredNurses(
				nurses.filter((nurse: any) => {
					return (
						nurse.nursename.toLowerCase().includes(searchInput) ||
						nurse.classx.toLowerCase().includes(searchInput)
					);
				}),
			);
		}
	}, [searchInput]);

	const handleSelect = (selectedId: any, selectedName: any, selectedClass: any) => {
		const isActivating = selectedId !== selectednurse_id;
		if (selectedId === selectednurse_id) {
			setSelectednurse_id("");
			setSelectedNurseName("");
		} else {
			setSelectednurse_id(selectedId);
			setSelectedNurseName(selectedName);
		}
		if (
			isActivating &&
			["Categoría III", "Categoría IV"].includes(selectedPatient.categoryname) &&
			selectedClass !== "ENF A"
		) {
			setOpenNurseCategoryAlert(true);
		}
	};

	const buildOptions = (): JSX.Element | JSX.Element[] => {
		if (filteredNurses === undefined) return <></>;

		return filteredNurses.map((nurse: any) => {
			let ntissCountFirstHalf = nurse.patients.filter(
				(patient: any) => patient.categoryid === "CC395F80-1753-4E31-ABE7-DD0EA15B6657",
			).length;
			let tissCountFirstHalf = nurse.patients.filter(
				(patient: any) => patient.categoryid === "9C582510-34A2-4A77-A415-436C95AF6839",
			).length;
			let sumFirstHalf = ntissCountFirstHalf + tissCountFirstHalf;
			let ntissCountSecondHalf = nurse.patients.filter(
				(patient: any) => patient.categoryid === "93F32C9D-9AA3-4672-A594-22D79131B454",
			).length;
			let tissCountSecondHalf = nurse.patients.filter(
				(patient: any) => patient.categoryid === "BC0C92FD-B7EB-4777-B8E9-88981D129667",
			).length;
			let sumSecondHalf = ntissCountSecondHalf + tissCountSecondHalf;

			return (
				<TableRow
					key={nurse.nurse_id}
					className="nurse-list-container__nurses__table__body__row"
					sx={{
						"& td": {
							backgroundColor:
								nurse.nurse_id === selectednurse_id ? variables.primary50 : "white",
						},
					}}
					onClick={() => {
						handleSelect(nurse.nurse_id, nurse.nursename, nurse.classx);
					}}
				>
					<TableCell className="nurse-list-container__nurses__table__body__cell --first">
						<Checkbox
							disableRipple
							icon={
								<Square className="nurse-list-container__nurses__table__body__cell__checkbox" />
							}
							checkedIcon={
								<SquareCheck className="nurse-list-container__nurses__table__body__cell__checkbox--selected" />
							}
							checked={nurse.nurse_id === selectednurse_id}
							onClick={() => {
								handleSelect(nurse.nurse_id, nurse.nursename, nurse.classx);
							}}
						/>
					</TableCell>
					<TableCell className="nurse-list-container__nurses__table__body__cell --large">
						<Typography>{nurse.nursename}</Typography>
					</TableCell>
					<TableCell className="nurse-list-container__nurses__table__body__cell --medium">
						<Typography>{nurse.classx}</Typography>
					</TableCell>
					<TableCell className="nurse-list-container__nurses__table__body__cell --xlarge">
						<Box>
							<Stack
								flex={2}
								spacing={2}
								sx={{ marginRight: "15px" }}
								direction={"row"}
							>
								<Box className="nurse-list-container__nurses__table__body__cell__numbers">
									<Tooltip
										title="Categoría I"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-I",
											arrow: "tooltip-styles--category-I--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--I">
											{
												nurse.patients.filter(
													(patient: any) =>
														patient.categoryid ===
														"29404BCA-B6D6-4FD2-A3A8-5A3663CF4552",
												).length
											}
										</Typography>
									</Tooltip>
									<Tooltip
										title="Categoría II"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-II",
											arrow: "tooltip-styles--category-II--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--II">
											{
												nurse.patients.filter(
													(patient: any) =>
														patient.categoryid ===
														"B7547881-1699-4A24-B1E1-FDA3C5A8552D",
												).length
											}
										</Typography>
									</Tooltip>
									<Tooltip
										title="Categoría III"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-III",
											arrow: "tooltip-styles--category-III--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--III">
											{
												nurse.patients.filter(
													(patient: any) =>
														patient.categoryid ===
														"A3C112AB-1756-4EC3-B3B6-C241DFD88025",
												).length
											}
										</Typography>
									</Tooltip>
									<Tooltip
										title="Categoría IV"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-IV",
											arrow: "tooltip-styles--category-IV--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--IV">
											{
												nurse.patients.filter(
													(patient: any) =>
														patient.categoryid ===
														"976C5E6B-0FF5-4FF4-8573-00E342F18E6D",
												).length
											}
										</Typography>
									</Tooltip>
								</Box>
								<Box className="nurse-list-container__nurses__table__body__cell__numbers">
									<Tooltip
										title="TISS 0-44 I NTISS 0-63"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-TISS",
											arrow: "tooltip-styles--category-TISS--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--TISS">
											{sumFirstHalf}
										</Typography>
									</Tooltip>
									<Tooltip
										title="TISS 45-88 I NTISS 64-127"
										placement="top"
										arrow
										classes={{
											tooltip: "tooltip-styles--category-NTISS",
											arrow: "tooltip-styles--category-NTISS--arrow",
										}}
									>
										<Typography className="nurse-list-container__nurses__table__body__cell__numbers__category--I">
											{sumSecondHalf}
										</Typography>
									</Tooltip>
								</Box>
							</Stack>
						</Box>
						<Typography
							sx={{
								color: `${variables.neutral400} !important`,
								fontWeight: "300 !important",
							}}
						>
							{" "}
							{nurse.patients.length} paciente
							{nurse.patients.length > 1 ? "s" : ""}
						</Typography>
					</TableCell>
				</TableRow>
			);
		});
	};

	const handleNext = () => {
		setSelectedPatient({
			...selectedPatient,
			nurse_id: selectednurse_id,
			nursename: selectedNurseName,
		});
		handleTab();
	};

	const dispatch = useDispatch();

	const returnAssistanceAdjust = () => {
		dispatch(setPageState(PageState.AssistanceAdjust));
		setOpenAssignNurseModal(false);
	};

	const handleSort = (key: string) => {
		let direction: "asc" | "desc" = "asc";

		if (sortConfig?.key === key && sortConfig.direction === "asc") {
			direction = "desc";
		}

		setSortConfig({ key, direction });

		if (key === "patients") {
			const sortedData = [...filteredNurses].sort((a: any, b: any) => {
				const countA = a.patients?.length || 0;
				const countB = b.patients?.length || 0;

				return direction === "asc" ? countA - countB : countB - countA;
			});
			setFilteredNurses(sortedData);
		} else {
			const sortedData = [...filteredNurses].sort((a: any, b: any) => {
				const valueA = a[key] ? a[key].toString().toUpperCase() : "";
				const valueB = b[key] ? b[key].toString().toUpperCase() : "";

				if (valueA < valueB) return direction === "asc" ? -1 : 1;
				if (valueA > valueB) return direction === "asc" ? 1 : -1;
				return 0;
			});
			setFilteredNurses(sortedData);
		}
	};

	return (
		<Box>
			{isLoading ? (
				<Box>
					<Box className="category-loader-container">
						<Box className="category-loader-container__loader"></Box>
					</Box>
				</Box>
			) : (
				<Box className="nurse-list-container">
					<Box className="modal-tab-content__patient-info">
						<Box sx={{ display: "flex" }}>
							<Typography className="modal-tab-content__patient-info__tag">
								Paciente:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								<User className="modal-tab-content__patient-info__info-user-icon" />
								{selectedPatient.name}
							</Typography>
							<Typography
								sx={{ marginLeft: "30px" }}
								className="modal-tab-content__patient-info__tag"
							>
								Cama:
							</Typography>
							<Typography className="modal-tab-content__patient-info__info">
								{selectedPatient.room}
							</Typography>
						</Box>
						{selectedPatient.tiss_value !== null ? (
							<Box sx={{ display: "flex" }}>
								<Typography className="modal-tab-content__patient-info__tag">
									Puntaje TISS:
								</Typography>
								<Typography className="modal-tab-content__patient-info__info">
									{selectedPatient.tiss_value}
								</Typography>
							</Box>
						) : selectedPatient.ntiss_value !== null ? (
							<Box sx={{ display: "flex" }}>
								<Typography className="modal-tab-content__patient-info__tag">
									Puntaje NTISS:
								</Typography>
								<Typography className="modal-tab-content__patient-info__info">
									{selectedPatient.ntiss_value}
								</Typography>
							</Box>
						) : (
							<Box sx={{ display: "flex" }}>
								<Typography className="modal-tab-content__patient-info__tag">
									Categoría:
								</Typography>
								<Typography className="modal-tab-content__patient-info__info">
									{selectedPatient.categoryname}
								</Typography>
							</Box>
						)}
					</Box>
					{isNursesEmpty ? (
						<Box className="empty-info-container">
							<EmtyInfoIcon className="empty-info-container__icon" />
							<Typography className="empty-info-container__title">
								Aún no hay profesionales en turno.
							</Typography>
							<Typography className="empty-info-container__description">
								Asegúrate de hacer tus ajustes de asistencia del turno
							</Typography>
							<Button
								className="empty-info-container__button"
								disableRipple
								onClick={() => returnAssistanceAdjust()}
							>
								<ArrowRight className="empty-info-container__button__icon" />
								<Typography className="empty-info-container__button__text">
									Ir a Ajustes de Asistencia
								</Typography>
							</Button>
						</Box>
					) : (
						<Box>
							<Box>
								<Box className="nurse-list-container__search-bar">
									<Search className="nurse-list-container__search-bar__icon" />
									<TextField
										variant="standard"
										className="nurse-list-container__search-bar__input"
										InputProps={{
											disableUnderline: true,
										}}
										placeholder="Buscar"
										onChange={(e) =>
											setSearchInput(e.target.value.toLowerCase())
										}
									/>
								</Box>
							</Box>
							<Box className="nurse-list-container__nurses">
								<Table className="nurse-list-container__nurses__table">
									<TableHead className="nurse-list-container__nurses__table__head">
										<TableCell>
											<Box className="nurse-list-container__nurses__table__head__cell" />
										</TableCell>
										<TableCell onClick={() => handleSort("nursename")}>
											<Box className="nurse-list-container__nurses__table__head__cell">
												<Typography>Profesional</Typography>
												{sortConfig?.key === "nursename" ? (
													sortConfig.direction === "desc" ? (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▾
														</Typography>
													) : (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▴
														</Typography>
													)
												) : (
													<Typography
														sx={{ color: variables.primary800 }}
													>
														▾
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell onClick={() => handleSort("classx")}>
											<Box className="nurse-list-container__nurses__table__head__cell">
												<Typography>Categoría</Typography>
												{sortConfig?.key === "classx" ? (
													sortConfig.direction === "desc" ? (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▾
														</Typography>
													) : (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▴
														</Typography>
													)
												) : (
													<Typography
														sx={{ color: variables.primary800 }}
													>
														▾
													</Typography>
												)}
											</Box>
										</TableCell>
										<TableCell onClick={() => handleSort("patients")}>
											<Box className="nurse-list-container__nurses__table__head__cell">
												<Typography>Pacientes</Typography>
												{sortConfig?.key === "classx" ? (
													sortConfig.direction === "desc" ? (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▾
														</Typography>
													) : (
														<Typography
															sx={{ color: variables.primary800 }}
														>
															▴
														</Typography>
													)
												) : (
													<Typography
														sx={{ color: variables.primary800 }}
													>
														▾
													</Typography>
												)}
											</Box>
										</TableCell>
									</TableHead>
									<TableBody className="nurse-list-container__nurses__table__body">
										{buildOptions()}
									</TableBody>
								</Table>
							</Box>
						</Box>
					)}
					<Box className="modal-footer">
						<Button
							variant="outlined"
							className="modal-footer-button"
							onClick={(e) => {
								handleNext(), e.currentTarget.blur();
							}}
							disableRipple
							disabled={isDisabled}
						>
							<Typography className="modal-footer-button-label">Siguiente</Typography>
							<ArrowRight className="modal-footer-button-icon" />
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
}
